import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { edvisorService, EdvisorMessage, EdvisorChatRequest } from '../services/edvisorService';
import { useAuth } from '../hooks/useAuth';
import { useUser } from './UserContext';
import { useSchools } from './SchoolsContext';
import { usePrograms } from './ProgramsContext';

interface ChatThread {
  threadId: string;
  title: string;
  lastMessage: string;
  timestamp: string;
  messageCount: number;
  isActive: boolean;
}

interface EdvisorContextType {
  messages: EdvisorMessage[];
  loading: boolean;
  error: string | null;
  threadId: string;
  sendMessage: (message: string) => Promise<void>;
  handleButtonClick: (buttonLabel: string) => Promise<void>;
  retryLastMessage: () => Promise<void>;
  refreshChatHistory: () => void;
  startNewChat: () => void;
  isTyping: boolean;
  // Chat History properties
  chatHistory: ChatThread[];
  showChatHistory: boolean;
  loadChatHistory: () => Promise<void>;
  selectChatThread: (threadId: string) => Promise<void>;
  toggleChatHistory: () => void;
  renameThread: (threadId: string, newTitle: string) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
}

const EdvisorContext = createContext<EdvisorContextType | undefined>(undefined);

export const useEdvisor = () => {
  const context = useContext(EdvisorContext);
  if (context === undefined) {
    throw new Error('useEdvisor must be used within an EdvisorProvider');
  }
  return context;
};

interface EdvisorProviderProps {
  children: ReactNode;
}

export const EdvisorProvider: React.FC<EdvisorProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<EdvisorMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState<string>('');
  const { user: authUser } = useAuth();
  
  // Chat History state
  const [chatHistory, setChatHistory] = useState<ChatThread[]>([]);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const { user } = useUser();
  
  // Pre-load school data for EdvisorContext (Phase 2 - Option 2)
  const { schools, loading: schoolsLoading } = useSchools();
  
  // Get ProgramsContext methods for eligibility data access
  const { getProgramEligibility } = usePrograms();

  // Function to extract school names from Edvisor response content (Phase 2 - Option 1)
  const extractSchoolNamesFromResponse = (response: string): string[] => {
    const schoolRegex = /!\[S\|([^|]+)\|([^\]]+)\]/g;
    const schoolNames: string[] = [];
    let match;
    
    while ((match = schoolRegex.exec(response)) !== null) {
      schoolNames.push(match[1].trim());
    }
    
    return schoolNames;
  };

  const fetchChatHistory = async () => {
    // Don't fetch if user is not authenticated
    if (!authUser) {
      setLoading(false);
      setMessages([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 EdvisorContext: Loading chat history for user:', authUser.uid);
      
      // Check if returning from EdVisor navigation
      const fromEdvisor = sessionStorage.getItem('fromEdvisor');
      const savedContext = sessionStorage.getItem('edvisorContext');
      
      if (fromEdvisor === 'true' && savedContext) {
        try {
          const context = JSON.parse(savedContext);
          // Check if context is recent (within 30 minutes) and user matches
          const isRecent = Date.now() - context.timestamp < 30 * 60 * 1000;
          const isSameUser = context.userId === authUser.uid;
          
          if (isRecent && isSameUser && context.messages && context.threadId) {
            console.log('✅ EdvisorContext: Restoring saved context:', { 
              threadId: context.threadId, 
              messageCount: context.messages.length,
              age: Math.round((Date.now() - context.timestamp) / 1000 / 60) + ' minutes'
            });
            
            setMessages(context.messages);
            setThreadId(context.threadId);
            setLoading(false);
            
            // Clean up session storage
            sessionStorage.removeItem('fromEdvisor');
            sessionStorage.removeItem('edvisorContext');
            return;
          } else {
            console.log('🔄 EdvisorContext: Saved context is stale or invalid:', {
              isRecent,
              isSameUser,
              hasMessages: !!context.messages,
              hasThreadId: !!context.threadId
            });
            // Clean up stale context
            sessionStorage.removeItem('fromEdvisor');
            sessionStorage.removeItem('edvisorContext');
          }
        } catch (error) {
          console.error('❌ EdvisorContext: Error restoring saved context:', error);
          // Clean up corrupted context
          sessionStorage.removeItem('fromEdvisor');
          sessionStorage.removeItem('edvisorContext');
        }
      }
      
      // Get active thread
      const activeThreadId = await edvisorService.getActiveThread();
      
      if (activeThreadId) {
        // Load existing chat history
        const chatHistory = await edvisorService.loadChatHistory(activeThreadId);
        
        if (chatHistory.length > 0) {
          setMessages(chatHistory);
          setThreadId(activeThreadId);
          console.log('✅ EdvisorContext: Loaded chat history:', { 
            threadId: activeThreadId, 
            messageCount: chatHistory.length 
          });
        } else {
          // No history, add welcome message
          addWelcomeMessage(activeThreadId);
        }
      } else {
        // No active thread, add welcome message (new thread will be created on first message)
        addWelcomeMessage();
      }
    } catch (err) {
      console.error('❌ EdvisorContext: Error loading chat history:', err);
      // Fallback to welcome message
      addWelcomeMessage();
      setError('Failed to load conversation history, but you can start a new conversation.');
    } finally {
      setLoading(false);
    }
  };

  const addWelcomeMessage = (existingThreadId?: string) => {
    const welcomeMessage = edvisorService.createMessage(
      `Hi ${user?.firstName || 'there'}! I'm Ed Visor, your Ai educational guidance counselor. I'm here to help you navigate Canadian post-secondary education, including school selection, program choices, and admission requirements. How can I assist you today?`,
      'edvisor',
      existingThreadId || ''
    );
    setMessages([welcomeMessage]);
    if (existingThreadId) {
      setThreadId(existingThreadId);
    }
  };

  const sendMessage = useCallback(async (messageContent: string) => {
    if (!messageContent.trim() || loading) return;

    // Check if user is authenticated
    if (!authUser) {
      setError('You must be signed in to use EdVisor chat.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setLastUserMessage(messageContent);

      // Add user message to the conversation
      const userMessage = edvisorService.createMessage(messageContent, 'user', threadId);
      setMessages(prev => [...prev, userMessage]);

      // Show typing indicator
      setIsTyping(true);

      // Send message to EdVisor
      const request: EdvisorChatRequest = {
        message: messageContent,
        threadId: threadId || undefined
      };

      const response = await edvisorService.sendMessage(request);

      // Update thread ID if it changed
      if (response.threadId !== threadId) {
        setThreadId(response.threadId);
      }

      // Add EdVisor response to the conversation
      const edvisorMessage = edvisorService.createMessage(
        response.response,
        'edvisor',
        response.threadId,
        response.eligibilityData,
        response.allProgramsEligibility,
        response.buttons
      );

      setMessages(prev => [...prev, edvisorMessage]);

      // Note: Eligibility data is now managed by ProgramsContext cache
      // No need to trigger calculations here - the cache will provide the data

      // Save thread metadata (title, lastMessage, etc.)
      await edvisorService.saveThreadMetadata(response.threadId, messageContent, response.response);

    } catch (err: any) {
      console.error('❌ EdvisorContext: Error sending message:', err);
      setError(err.message || 'Failed to send message. Please try again.');
      
      // Add error message to conversation
      const errorMessage = edvisorService.createMessage(
        'Sorry, I encountered an error processing your message. Please try again.',
        'edvisor',
        threadId
      );
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  }, [loading, threadId, authUser]);

  const handleButtonClick = useCallback(async (buttonLabel: string) => {
    // Send the button label as a message
    await sendMessage(buttonLabel);
  }, [sendMessage]);


  const retryLastMessage = useCallback(async () => {
    if (!lastUserMessage) return;
    
    console.log('🔄 EdvisorContext: Retrying last message:', lastUserMessage);
    
    // Remove the last error message if it exists
    setMessages(prev => {
      const filtered = prev.filter((msg, index) => {
        if (index === prev.length - 1 && msg.sender === 'edvisor' && 
            msg.content.includes('Sorry, I encountered an error')) {
          return false;
        }
        return true;
      });
      return filtered;
    });

    // Retry sending the last message
    await sendMessage(lastUserMessage);
  }, [lastUserMessage, sendMessage]);

  const refreshChatHistory = () => {
    fetchChatHistory();
  };

  // Helper function to save current context to sessionStorage
  const saveContextToSessionStorage = () => {
    if (typeof window !== 'undefined' && messages.length > 0 && threadId && authUser?.uid) {
      try {
        const context = {
          messages,
          threadId,
          userId: authUser.uid,
          timestamp: Date.now()
        };
        
        const contextString = JSON.stringify(context);
        const contextSize = new Blob([contextString]).size;
        
        // Check if context is too large (limit to 1MB)
        if (contextSize > 1024 * 1024) {
          console.warn('⚠️ EdvisorContext: Context too large, truncating messages:', {
            originalSize: contextSize,
            messageCount: messages.length
          });
          
          // Keep only the last 20 messages to reduce size
          const truncatedMessages = messages.slice(-20);
          const truncatedContext = {
            ...context,
            messages: truncatedMessages
          };
          
          sessionStorage.setItem('edvisorContext', JSON.stringify(truncatedContext));
          console.log('🔄 EdvisorContext: Saved truncated context to sessionStorage:', {
            threadId,
            originalMessageCount: messages.length,
            truncatedMessageCount: truncatedMessages.length
          });
        } else {
          sessionStorage.setItem('edvisorContext', contextString);
          console.log('🔄 EdvisorContext: Saved context to sessionStorage:', {
            threadId,
            messageCount: messages.length,
            size: contextSize
          });
        }
      } catch (error) {
        console.error('❌ EdvisorContext: Error saving context to sessionStorage:', error);
      }
    }
  };

  // Helper function to clear context from sessionStorage
  const clearContextFromSessionStorage = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('edvisorContext');
      sessionStorage.removeItem('fromEdvisor');
      console.log('🔄 EdvisorContext: Cleared context from sessionStorage');
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, [user]);

  // Pre-load school data when EdvisorContext mounts (Phase 2 - Option 2)
  useEffect(() => {
    if (!schoolsLoading && schools.length > 0) {
      console.log('🔄 EdvisorContext: School data pre-loaded:', schools.length, 'schools');
    } else if (schoolsLoading) {
      console.log('🔄 EdvisorContext: Loading school data...');
    }
  }, [schools, schoolsLoading]);

  // Expose current context to window for message parser access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.edvisorMessages = messages;
      window.edvisorThreadId = threadId;
      window.edvisorUserId = user?.uid;
      
      console.log('🔄 EdvisorContext: Window object updated', {
        messagesCount: messages.length,
        threadId: threadId,
        userId: user?.uid,
        hasMessages: !!window.edvisorMessages,
        hasThreadId: !!window.edvisorThreadId,
        hasUserId: !!window.edvisorUserId
      });
    }
  }, [messages, threadId, user?.uid]);

  // Save context before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveContextToSessionStorage();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [messages, threadId, user?.uid]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up session storage if not navigating to a profile page
      const fromEdvisor = sessionStorage.getItem('fromEdvisor');
      if (!fromEdvisor) {
        sessionStorage.removeItem('edvisorContext');
      }
    };
  }, []);

  const loadChatHistory = useCallback(async () => {
    if (!authUser) {
      setChatHistory([]);
      return;
    }

    try {
      console.log('🔄 EdvisorContext: Loading chat history for user:', authUser.uid);
      
      const threads = await edvisorService.getAllUserThreads();
      
      // Mark current thread as active
      const updatedThreads = threads.map(thread => ({
        ...thread,
        isActive: thread.threadId === threadId
      }));
      
      setChatHistory(updatedThreads);
      console.log('✅ EdvisorContext: Loaded chat history:', updatedThreads.length, 'threads');
      console.log('🔍 Debug - Context thread details:', updatedThreads.map(t => ({
        threadId: t.threadId,
        title: t.title,
        messageCount: t.messageCount,
        isActive: t.isActive
      })));
    } catch (err) {
      console.error('❌ EdvisorContext: Error loading chat history:', err);
      setChatHistory([]);
    }
  }, [authUser, threadId]);

  const selectChatThread = useCallback(async (selectedThreadId: string) => {
    if (selectedThreadId === threadId) {
      // Already viewing this thread
      setShowChatHistory(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 EdvisorContext: Switching to thread:', selectedThreadId);
      
      const threadMessages = await edvisorService.loadSpecificThread(selectedThreadId);
      
      setMessages(threadMessages);
      setThreadId(selectedThreadId);
      setShowChatHistory(false);
      
      // Update chat history to mark selected thread as active
      setChatHistory(prev => prev.map(thread => ({
        ...thread,
        isActive: thread.threadId === selectedThreadId
      })));
      
      console.log('✅ EdvisorContext: Switched to thread:', selectedThreadId);
    } catch (err) {
      console.error('❌ EdvisorContext: Error switching to thread:', err);
      setError('Failed to load chat thread');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  const toggleChatHistory = useCallback(() => {
    console.log('🔄 EdvisorContext: Toggling chat history, current state:', showChatHistory);
    setShowChatHistory(prev => !prev);
    if (!showChatHistory) {
      // Load chat history when opening dropdown
      console.log('🔄 EdvisorContext: Loading chat history...');
      loadChatHistory();
    }
  }, [showChatHistory, loadChatHistory]);

  const startNewChat = useCallback(() => {
    // Clear current messages and thread
    setMessages([]);
    setThreadId('');
    setError(null);
    setLastUserMessage('');
    
    // Add welcome message for new chat
    addWelcomeMessage();
    
    // Update chat history to mark no thread as active
    setChatHistory(prev => prev.map(thread => ({
      ...thread,
      isActive: false
    })));
    
    console.log('🔄 EdvisorContext: Started new chat');
  }, []);

  const renameThread = useCallback(async (threadId: string, newTitle: string) => {
    try {
      console.log('🔄 EdvisorContext: Renaming thread', threadId, 'to', newTitle);
      
      await edvisorService.renameThread(threadId, newTitle);
      
      // Update the chat history to reflect the new title
      setChatHistory(prev => prev.map(thread => 
        thread.threadId === threadId 
          ? { ...thread, title: newTitle }
          : thread
      ));
      
      console.log('✅ EdvisorContext: Successfully renamed thread');
    } catch (error) {
      console.error('❌ EdvisorContext: Error renaming thread:', error);
      throw error;
    }
  }, []);

  const deleteThread = useCallback(async (threadIdToDelete: string) => {
    try {
      console.log('🔄 EdvisorContext: Deleting thread', threadIdToDelete);
      
      await edvisorService.deleteThread(threadIdToDelete);
      
      // Remove the thread from the chat history
      setChatHistory(prev => prev.filter(thread => thread.threadId !== threadIdToDelete));
      
      // If the deleted thread was the current thread, start a new chat
      if (threadIdToDelete === threadId) {
        startNewChat();
      }
      
      console.log('✅ EdvisorContext: Successfully deleted thread');
    } catch (error) {
      console.error('❌ EdvisorContext: Error deleting thread:', error);
      throw error;
    }
  }, [startNewChat, threadId]);

  const value: EdvisorContextType = {
    messages,
    loading,
    error,
    threadId,
    sendMessage,
    handleButtonClick,
    retryLastMessage,
    refreshChatHistory,
    startNewChat,
    isTyping,
    // Chat History properties
    chatHistory,
    showChatHistory,
    loadChatHistory,
    selectChatThread,
    toggleChatHistory,
    renameThread,
    deleteThread,
  };

  return (
    <EdvisorContext.Provider value={value}>
      {children}
    </EdvisorContext.Provider>
  );
};
